import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { trackEvent } from 'helpers/analytics';
import { useSetSignupFormActiveMutation } from '../../../../services/signupForms';
import { useCreateSourcingCampaignMutation } from '../../../../services/findemV2';

export function useEnabledState({ sharedList, formConfig }) {
  const [showFormBuilder, setShowFormBuilder] = useState(false);
  const [showSuccessEditor, setShowSuccessEditor] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isGrowListAgentOpen, setIsGrowListAgentOpen] = useState(false);
  const [agentCampaign, setAgentCampaign] = useState(null);

  const [setSignupFormActive, { isLoading: isDisabling }] = useSetSignupFormActiveMutation();
  const [createSourcingCampaign, { isLoading: isCreatingCampaign }] = useCreateSourcingCampaignMutation();

  const networkId = sharedList?.collection?.id;
  const signupFormUrl = `${window.location.origin}/${networkId}/shared-lists/${sharedList?.slug || 'list-slug'}/signup`;

  const handleCopyLink = useCallback(() => {
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(signupFormUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [signupFormUrl]);

  const handleDisableSignupForm = useCallback(async () => {
    if (!formConfig?.id) return;

    try {
      await setSignupFormActive({
        collectionId: sharedList.collection?.id,
        sharedListId: sharedList.id,
        formId: formConfig.id,
        active: false,
      }).unwrap();
      trackEvent('contacts:list:disable_signup_form', {
        collection_id: sharedList.collection?.id,
        list_id: sharedList.id,
      });
    } catch (error) {
      toast.error(`Failed to disable signup form. Please try again or contact support.`);
    }
  }, [setSignupFormActive, formConfig?.id, sharedList.collection?.id, sharedList.id]);

  const openFormBuilder = useCallback(() => setShowFormBuilder(true), []);
  const closeFormBuilder = useCallback(() => setShowFormBuilder(false), []);
  const openSuccessEditor = useCallback(() => setShowSuccessEditor(true), []);
  const closeSuccessEditor = useCallback(() => setShowSuccessEditor(false), []);

  const handleSetupAgent = useCallback(async () => {
    trackEvent('contacts:list:agent_click_form_tab', {
      shared_list_id: sharedList?.id,
      list_name: sharedList?.name,
      collection_id: sharedList?.collection?.id,
    });

    const existingCampaign = sharedList?.sourcingCampaign;

    if (existingCampaign?.sharedListId) {
      setAgentCampaign(existingCampaign);
      setIsGrowListAgentOpen(true);
    } else {
      try {
        const campaign = await createSourcingCampaign({
          sharedListId: sharedList?.id,
        }).unwrap();

        setAgentCampaign(campaign);
        setIsGrowListAgentOpen(true);
      } catch (error) {
        toast.error('Failed to create campaign. Please try again.');
      }
    }
  }, [sharedList, createSourcingCampaign]);

  const handleCloseGrowListAgent = useCallback(() => {
    setIsGrowListAgentOpen(false);
    setAgentCampaign(null);
  }, []);

  const hasAgent = Boolean(sharedList?.sourcingCampaign?.sharedListId);

  return {
    showFormBuilder,
    showSuccessEditor,
    copied,
    isDisabling,
    signupFormUrl,
    handleCopyLink,
    handleDisableSignupForm,
    handleSetupAgent,
    openFormBuilder,
    closeFormBuilder,
    openSuccessEditor,
    closeSuccessEditor,
    isGrowListAgentOpen,
    agentCampaign,
    handleCloseGrowListAgent,
    isCreatingCampaign,
    hasAgent,
  };
}
